import { Chip, Tooltip, visuallyHidden } from '@montugroup/design-system';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { styled } from '@mui/material';
import type { MouseEvent } from 'react';
import { Fragment, useId } from 'react';

import type { Latin1SafeTextFieldProps } from '@/components/fields';
import { Latin1SafeTextField } from '@/components/fields';
import {
  DOSAGE_CHIP_LAYER_TEST_ID,
  DOSAGE_SUFFIX_CHIPS_DESCRIPTION,
  DOSAGE_SUFFIX_CHIP_TEST_ID,
} from '../MedicationPadItemEditor.constants';

const DOSAGE_ROWS = 3;

// Both children share this cell, so the textarea is stretched to the height of the chip layer
const STACKED_CELL = '1 / 1 / 2 / 2';

/**
 * Repaints the textarea's value invisibly so the chip lands exactly where the typed text ends, and sizes the input so
 * it grows to fit both. Metrics are inherited from the input root rather than restated, so the two layers always
 * wrap at the same column.
 */
const ChipLayer = styled('div')({
  gridArea: STACKED_CELL,
  zIndex: 1,
  minHeight: `${DOSAGE_ROWS}lh`,
  font: 'inherit',
  letterSpacing: 'inherit',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'break-word',
  color: 'transparent',
  pointerEvents: 'none',
  userSelect: 'none',
});

// The layer above ignores the pointer so typing passes through to the textarea; the chip alone takes it back so its
// tooltip can open on hover. It takes focus too, so the tooltip opens from the keyboard
const SuffixChip = styled(Chip)({
  verticalAlign: 'top',
  pointerEvents: 'auto',
});

// A grid rather than absolute positioning: a textarea keeps its intrinsic `rows` height when positioned, but is
// stretched like any other grid item. The column may shrink below its content, so a long unbroken word wraps rather
// than widening the field
const chipTextAreaSx = {
  '& .MuiInputBase-root': {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    alignItems: 'stretch',
  },
  '& .MuiInputBase-input': {
    gridArea: STACKED_CELL,
    overflow: 'hidden',
  },
} as const;

type DosageSuffixChip = {
  /** The limit the chip states; one chip per kind, so it keys the chip */
  kind: string;
  label: string;
  tooltip: string;
};

type TextFieldSlotProps = NonNullable<Latin1SafeTextFieldProps['slotProps']>;

/** Each slot's props as an object: the chips merge their own props into them, which a callback form would not take */
type DosageFieldSlotProps = {
  [Slot in keyof TextFieldSlotProps]?: Exclude<TextFieldSlotProps[Slot], (...args: never[]) => unknown>;
};

export type DosageFieldProps = Omit<Latin1SafeTextFieldProps, 'value' | 'multiline' | 'rows' | 'sx' | 'slotProps'> & {
  value: string;
  /** Read-only phrases pinned to the end of the typed text, e.g. the maximum daily dose. Not part of `value`. */
  suffixChips?: DosageSuffixChip[];
  slotProps?: DosageFieldSlotProps;
};

type DosageSuffixChipsProps = Pick<DosageFieldProps, 'value' | 'disabled'> & {
  id: string;
  suffixChips: DosageSuffixChip[];
};

/** A press on a chip types after the text it is pinned to: focus goes to the textarea, with the caret at the end */
const focusTextEnd = (event: MouseEvent, id: string) => {
  const input = document.getElementById(id);
  if (!(input instanceof HTMLTextAreaElement)) {
    return;
  }
  event.preventDefault();
  input.focus();
  input.setSelectionRange(input.value.length, input.value.length);
};

/** The chips pinned after the typed text, and the description that tells screen readers what they append */
const DosageSuffixChips = ({ id, value, disabled, suffixChips }: DosageSuffixChipsProps) => (
  <>
    {/* The chips sit outside the textarea, so its description says what they append to the text. Hidden from the
    reading order, which reaches the chips themselves; aria-describedby still reads it */}
    <span id={`${id}-suffix-chips`} style={visuallyHidden} aria-hidden>
      {DOSAGE_SUFFIX_CHIPS_DESCRIPTION} {suffixChips.map(({ label, tooltip }) => `${label}. ${tooltip}`).join(' ')}
    </span>
    <ChipLayer data-testid={DOSAGE_CHIP_LAYER_TEST_ID}>
      <span aria-hidden>
        {value}
        {value && ' '}
      </span>
      {suffixChips.map(({ kind, label, tooltip }, index) => (
        <Fragment key={kind}>
          {index > 0 && ' '}
          <Tooltip title={tooltip} describeChild>
            <SuffixChip
              size="small"
              tabIndex={disabled ? -1 : 0}
              onMouseDown={(event) => focusTextEnd(event, id)}
              icon={<InfoOutlinedIcon />}
              label={label}
              disabled={disabled}
              data-testid={DOSAGE_SUFFIX_CHIP_TEST_ID}
              data-dd-privacy="allow"
            />
          </Tooltip>
        </Fragment>
      ))}
    </ChipLayer>
  </>
);

/**
 * Dosage textarea which can pin non-editable chips to the end of the typed text.
 */
export const DosageField = ({ value, suffixChips, disabled, slotProps, ...rest }: DosageFieldProps) => {
  const generatedId = useId();
  const id = rest.id ?? generatedId;

  return (
    <Latin1SafeTextField
      {...rest}
      id={id}
      value={value}
      disabled={disabled}
      multiline
      rows={DOSAGE_ROWS}
      sx={suffixChips?.length ? chipTextAreaSx : undefined}
      slotProps={
        suffixChips?.length
          ? {
              ...slotProps,
              // MUI's autosizing textarea writes an inline height, which would stop the grid stretching it; a plain
              // textarea carries no `type`
              htmlInput: {
                ...slotProps?.htmlInput,
                type: undefined,
                // Replaces the helper text link MUI would set, so it is kept beside the caller's and the chips'
                'aria-describedby': [
                  slotProps?.htmlInput?.['aria-describedby'],
                  `${id}-helper-text`,
                  `${id}-suffix-chips`,
                ]
                  .filter(Boolean)
                  .join(' '),
              },
              input: {
                ...slotProps?.input,
                inputComponent: 'textarea',
                endAdornment: <DosageSuffixChips id={id} value={value} disabled={disabled} suffixChips={suffixChips} />,
              },
            }
          : slotProps
      }
    />
  );
};
